import React, { useEffect, useState } from 'react';
import { Container } from 'react-bootstrap';
import Filter from '../components/Filter';
import ReleasedTable from '../components/ReleasedTable';
import { GridColDef } from '@mui/x-data-grid';
import { useRelease } from '../../../apis/release/ReleaseProvider';
import { getPageName } from '../../../locales/translationUtils';
import { useTranslation } from 'react-i18next';
import { useCargoSearchList } from '../../../apis/cargoSearchList/CargoSearchListProvider';
import i18n from '../../../i18n';
import { useNavigate } from 'react-router-dom';
import NavBar, { NavBarItem } from "../../../components/NavBar";
import { downloadContent } from '../../../services/contentService';
import AlertOverlay from '../../../components/Alert';
import CargoContentList from '../../../components/CargoContentList';
import { Content } from '../../Release/types';

const ReleasedListView: React.FC = () => {
  const { t } = useTranslation();
  const [alert, setAlert] = useState<{
      message: string;
      variant: "success" | "danger" | "warning" | "info";
  } | null>(null);
  const navigate = useNavigate();
  const { releases, fetchReleases, apiResponse } = useRelease();
  const { cargosSearchList, fetchCargoSearchList } = useCargoSearchList();
  const [filters, setFilters] = useState<Record<string, string>>({
    from: '',
    arrivalDate: '',
    pageNum: "0",
    quantityPerPage: "20"
  });

  const [paginationModel, setPaginationModel] = React.useState({
    pageSize: 20,
    page: 0,
  });

  useEffect(() => {
    fetchCargoSearchList();
  }, [fetchCargoSearchList]);

  useEffect(() => {
    if (cargosSearchList && cargosSearchList.length > 0) {
      const initialFilters = Object.fromEntries(
        cargosSearchList.map(({ key }: { key: string }) => [key, ""])
      );

      // Campos de data que precisam de período (case-insensitive)
      const dateFieldsLower = ['arrivaldate', 'pu', 'input'];
      
      // Adicionar campos de período para cada campo de data encontrado
      const dateFilters: Record<string, string> = {
        arrivalDateInicio: '',
        arrivalDateFim: '',
      };
      
      cargosSearchList.forEach(({ key }: { key: string }) => {
        if (dateFieldsLower.includes(key.toLowerCase()) && key.toLowerCase() !== 'arrivaldate') {
          dateFilters[`${key}Inicio`] = '';
          dateFilters[`${key}Fim`] = '';
        }
      });

      setFilters(() => ({
        ...initialFilters,
        ...dateFilters,
        from: '',
        arrivalDate: '',
        pageNum: "0",
        quantityPerPage: "20"
      }));
    }
  }, [cargosSearchList]);
  
  useEffect(() => {
    const newFilters = { 
      ...filters, 
      pageNum: paginationModel.page.toString(),
      quantityPerPage: paginationModel.pageSize.toString()
    };
    setFilters(newFilters);
    fetchReleases(newFilters, true);
  }, [paginationModel]);

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleSearch = () => {
    const newFilters = { 
      ...filters, 
      pageNum: "0",
      quantityPerPage: "20"
    };
    setFilters(newFilters);
    fetchReleases(newFilters, true);
  };
  
  const handleDownload = async (content: Pick<Content, 'id' | 'description'>) => {
    try {
      const response = await downloadContent(content.id);
      
      const downloadUrl = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `${content.description}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
        setAlert({ message: `${t('release.labels.downloadError')} ${content.description}`, variant: "danger" });
    }
  };

  const navItems: NavBarItem[] = [
    { label: t('release.labels.home'), route: "/", iconName: "house-door" },
    { label: t('release.labels.list'), route: "/release", iconName: "table" },
  ];

  const dateFormatter = new Intl.DateTimeFormat(i18n?.language ?? 'pt');

  const columns: GridColDef[] = [
    { 
      field: 'id', 
      headerName: t('release.fields.id'), 
      width: 80, 
      renderCell: (params) => (
          <span
              style={{ color: "black", cursor: "pointer" }}
              onClick={() => navigate(`/release/released/${params.row.release?.id}`)}>
              {params.row.release?.id}
          </span>
      ),
    },
    { field: 'fromIdentification', headerName: t('release.fields.from'), width: 100 },
    { field: 'clientName', headerName: t('release.fields.client'), width: 120 },
    { field: 'supplierName', headerName: t('release.fields.supplier'), width: 350 },
    { field: 'wrNumber', headerName: t('release.fields.wrNumber'), width: 120 },
    { field: 'poNumber', headerName: t('release.fields.poNumber'), width: 120 },
    {
      field: 'piecesWeight',
      headerName: t('release.fields.piecesWeight'),
      width: 120,
      renderCell: (params: any) => {
        const { pieces, weight } = params.row;
        return `${pieces} / ${weight}`;
      },
    },
    { field: 'dawb', headerName: t('release.fields.dawb'), width: 120 },
    {
      field: 'invoice',
      headerName: t('release.fields.invoice'),
      width: 220,
      renderCell: (params: any) => {
        const info = params?.row?.carrierInformation?.[0];
        const invoice = info?.invoice ?? '-';
        const documentKey = info?.document?.key ?? '-';
        return `${invoice} / ${documentKey}`;
      }
    },
    {
      field: 'pu',
      headerName: t('release.fields.pu'),
      type: "date",
      width: 120,
      valueFormatter: (params: any) => {
        if (!params) return null;
        const date = new Date(params);
        return isNaN(date.getTime()) ? null : dateFormatter.format(date);
      }
    },
    {
      field: 'input',
      headerName: t('release.fields.input'),
      type: "date",
      width: 120,
      valueFormatter: (params: any) => {
        if (!params) return null;
        const date = new Date(params);
        return isNaN(date.getTime()) ? null : dateFormatter.format(date);
      }
    },
    {
      field: 'content',
      headerName: t('release.fields.content'),
      width: 500,
      minWidth: 200,
      renderCell: (params: any) => (
        <CargoContentList
          cargoContents={params.row?.cargoContents ?? []}
          onDownload={handleDownload}
          downloadTitle={t('release.labels.clickToDownload')}
          expandTitle={t('release.labels.showAllFiles')}
        />
      )
    },
    {
      field: 'to', headerName: t('release.fields.to'), width: 180, renderCell: (params: any) => {
        return params?.row?.release?.to;
      }
    },
    {
      field: 'releasedBy', headerName: t('release.fields.releasedBy'), width: 160, renderCell: (params: any) => {
        return params?.row?.release?.releasedBy;
      }
    },
    {
      field: 'releasedFormatted',
      headerName: t('release.fields.released'),
      width: 120,
      renderCell: (params: any) => {
        return params?.row?.release?.releasedFormatted;
      }
    }
  ];

  const dynamicFilters = Object.entries(filters).filter(
    ([key]) => key !== 'from' && key !== 'arrivalDate'
  );

  const reorderedFilters = [
    ...dynamicFilters,
    ['from', filters.from],
    ['arrivalDate', filters.arrivalDate],
  ];

  return (
    <Container fluid style={{ height: 'calc(90vh - 90px)', overflow: 'auto'}}>
      <NavBar items={navItems} />
      {alert && (
          <AlertOverlay
              message={alert.message}
              variant={alert.variant}
              onClose={() => setAlert(null)}
          />
      )}
      <div className="mb-3"></div>
      <h2>{getPageName('released')}</h2>
      <div className="mb-3"></div>
      <Filter
        filters={Object.fromEntries(reorderedFilters)}
        onFilterChange={handleFilterChange}
        onSearch={handleSearch}
      />
      <ReleasedTable 
        apiResponse={apiResponse} 
        rows={releases ?? []} 
        columns={columns} 
        paginationModel={paginationModel}
        setPaginationModel={setPaginationModel}
      />
    </Container>
  );
};

export default ReleasedListView;
